import {
    ActionCreatorWithPayload,
    CaseReducer,
    createAsyncThunk,
} from '@reduxjs/toolkit'
import {
    NinchatClientPromiseValue,
    NinchatEventActionPayload,
} from '../NinchatClient'
import {
    NinchatEventType,
    NinchatMessageReceivedEvent,
} from '../types/events'
import {
    NinchatInfoMessageTypes,
    NinchatMessageType,
    NinchatMessageTypes,
} from '../types/messages'
import {
    NinchatState,
    NinchatStore,
} from 'api/types'
import HighlightRegExp from 'utils/HighlightRegExp'
import {RootState} from '../../store'
import {extractProperties} from '.'
import {isRealmOwnerOrOperator} from 'utils/realm/realm'

const eventTypePrefix = 'NINCHAT/event'

type MessageReceivedAsyncThunk = typeof thunk & {meta: typeof meta, reducer: CaseReducer<NinchatState>} & MessageReceivedActionCreator

type MessageReceivedActionCreator = ActionCreatorWithPayload<NinchatEventActionPayload<NinchatMessageReceivedEvent>, 'NINCHAT/event/message_received'>

type MessageReceivedAsyncThunkConfig = {
    fulfilledMeta: MessageReceivedMeta
    state: RootState
}

type MessageReceivedEvent = NinchatMessageReceivedEvent | NinchatEventActionPayload<NinchatMessageReceivedEvent>

type MessageReceivedMeta = {
    fromHistory: boolean
    fromSelf: boolean
    isHighlighting: boolean
    isInfoMessage: boolean
    isLocal: boolean
}

const thunk = createAsyncThunk<NinchatClientPromiseValue, NinchatEventActionPayload<NinchatMessageReceivedEvent>, MessageReceivedAsyncThunkConfig>(
    `${eventTypePrefix}/${NinchatEventType.NinchatMessageReceivedEvent}`,
    (event, thunkAPI) => thunkAPI.fulfillWithValue(event, meta(thunkAPI.getState().ninchat, event)), // eslint-disable-line @typescript-eslint/no-use-before-define
)

export const messageReceived = thunk as MessageReceivedAsyncThunk

const meta = (state: NinchatState, payload: MessageReceivedEvent): MessageReceivedMeta => {
    const event = typeof payload.event === 'string' ? payload : payload.event
    const messagePayload = typeof payload.event === 'string' ? {} : payload.payload

    const {
        channel_id, // eslint-disable-line @typescript-eslint/no-unused-vars
        user_id, // eslint-disable-line @typescript-eslint/no-unused-vars
        history_length,
        ...messageProperties
    } = extractProperties<NinchatMessageReceivedEvent>(event)

    const message: NinchatStore.NinchatStoreMessage = {
        ...messagePayload,
        ...messageProperties,
        _revision: Date.now(),
    }

    const highlights = new Set(state.user_settings.highlight)

    if (state.user_settings.highlight_name && state.user_attrs.name) {
        highlights.add(state.user_attrs.name)
    }

    const hre = new HighlightRegExp([...highlights], 'giu')

    const fromHistory = typeof history_length === 'number'
    const fromSelf = message.message_user_id === state.user_id
    const isHighlighting = hre.test(message)
    const isInfoMessage = Object.values<NinchatMessageType>(NinchatInfoMessageTypes).includes(message.message_type)
    const isLocal = !!event.action_id

    return {
        fromHistory,
        fromSelf,
        isHighlighting,
        isInfoMessage,
        isLocal,
    }
}

messageReceived.meta = meta

messageReceived.reducer = (state, action) => { // eslint-disable-line complexity
    const {payload} = action
    const event = typeof payload.event === 'string' ? payload : payload.event
    const messagePayload = typeof payload.event === 'string' ? {} : payload.payload

    const {
        fromHistory,
        fromSelf,
        isHighlighting,
        isInfoMessage,
        isLocal,
        requestId,
        requestStatus,
    } = action.meta

    const {
        channel_id,
        user_id,
        history_length, // eslint-disable-line @typescript-eslint/no-unused-vars
        ...messageProperties
    } = extractProperties<NinchatMessageReceivedEvent>(event)

    const message: NinchatStore.NinchatStoreMessage = {
        ...messagePayload,
        ...messageProperties,
        _revision: Date.now(),
    }

    const keepStatus = fromHistory || fromSelf

    if (channel_id) {
        if (message.message_type === NinchatMessageTypes.NinchatMetadataMessage) {
            state.user_channels[channel_id] ??= {channel_attrs: {}} as NinchatStore.NinchatStoreUserChannel
            const channel = state.user_channels[channel_id]
            channel.channel_attrs ??= {}

            if (!fromHistory && message.message_user_id) {
                channel.member_message_metadata ??= {}
                channel.member_message_metadata[message.message_user_id] ??= {}

                const metadata = channel.member_message_metadata[message.message_user_id]

                Object.entries(message.data ?? {}).forEach(([key, value]) => {
                    metadata[key] = value
                })
            }

            if (message.message_fold && message.data && ('tag_ids' in message.data || 'Notes' in message.data)) {
                // Don't display Notes or Tags in the backlog
                return
            }

            if (message.data && 'post_answers' in message.data) {
                // Only realm owners and operators can see post answers
                const canViewPostAnswers = isRealmOwnerOrOperator(state, channel.realm_id)

                if (!canViewPostAnswers) {
                    return
                }
            }
        }

        state._messages[channel_id] ??= {messages: []}

        const {messages} = state._messages[channel_id]

        if (fromHistory) {
            messages.unshift(message)
        } else if (isLocal && fromSelf) {
            const messageIndex = messages.findIndex((m: NinchatStore.NinchatStoreMessage) => m.message_id === requestId)
            if (messageIndex >= 0) {
                messages[messageIndex] = {
                    ...messages[messageIndex],
                    ...message,
                    _message_status: requestStatus,
                }
            } else {
                messages.push(message)
            }
        } else {
            messages.push(message)
        }

        messages.sort((a, b) => (a.message_id < b.message_id ? -1 : a.message_id > b.message_id ? 1 : 0))
        state._messages[channel_id].messages = messages.filter((x, i, a) => !i || x.message_id !== a[i - 1].message_id)

        if (state.user_channels[channel_id]) {
            const {
                channel_attrs,
                channel_status,
            } = state.user_channels[channel_id]

            const hasHighlight = channel_status === 'highlight'
            const isAudience = !!channel_attrs?.audience_id

            /* eslint-disable indent, multiline-ternary, no-negated-condition */
        const channelStatus = keepStatus ? channel_status
            : hasHighlight || isHighlighting ? 'highlight'
            : isAudience && !isInfoMessage ? 'highlight'
            : isAudience && isInfoMessage ? 'unread'
            : !isInfoMessage ? 'unread'
            : channel_status
        /* eslint-enable */

            state.user_channels[channel_id].channel_status = channelStatus
        }
    }

    if (user_id) {
        state.user_dialogues[user_id] ??= {}
        state._messages[user_id] ??= {messages: []}

        const {messages} = state._messages[user_id]

        if (fromHistory) {
            messages.unshift(message)
        } else if (isLocal && fromSelf) {
            const messageIndex = messages.findIndex((m: NinchatStore.NinchatStoreMessage) => m.message_id === requestId)
            if (messageIndex >= 0) {
                messages[messageIndex] = {
                    ...message,
                    _message_status: requestStatus,
                }
            }
        } else {
            messages.push(message)
        }

        messages.sort((a, b) => (a.message_id < b.message_id ? -1 : a.message_id > b.message_id ? 1 : 0))
        state._messages[user_id].messages = messages.filter((x, i, a) => !i || x.message_id !== a[i - 1].message_id)

        const {dialogue_status} = state.user_dialogues[user_id]

        const hasHighlight = dialogue_status === 'highlight'

        /* eslint-disable indent, multiline-ternary, no-negated-condition */
        const dialogueStatus = keepStatus ? dialogue_status
            : hasHighlight || isHighlighting ? 'highlight'
            : !isInfoMessage ? 'highlight'
            : isInfoMessage ? 'unread'
            : dialogue_status
        /* eslint-enable */

        state.user_dialogues[user_id].dialogue_status = dialogueStatus

        if (!fromSelf && message.message_user_name) {
            state._users[user_id] ??= {
                user_attrs: {},
                user_identities: {},
            }

            state._users[user_id].user_attrs.name = message.message_user_name
        }
    }
}
